import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { httpClient } from '../../../services/httpClient';
import { Sketch } from '@uiw/react-color';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

type LiveChatConfigRow = {
  id: string;
  configuration: {
    app_id?: string;
    [k: string]: any;
  };
};

export default function LiveChatSetupForm() {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<LiveChatConfigRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [domainInputs, setDomainInputs] = useState<string[]>(['']);
  const [bundleIdInputs, setBundleIdInputs] = useState<string[]>(['']);
  const [styleOverride, setStyleOverride] = useState({
    accentColor: '',
    roundness: 'full',
    title: t('atrium.liveChat.defaultWidgetTitle', 'Live support'),
    position: 'bottom-right',
    horizontalOffset: 16,
    verticalOffset: 16
  });

  const appId = config?.configuration?.app_id || '';
  const configuredDomains = (config?.configuration?.domains as string[] | undefined) || [];
  const configuredBundleIds = (config?.configuration?.bundleIds as string[] | undefined) || [];

  const snippet = useMemo(() => {
    if (!appId) return '';
    return `<script>
  // This block is necessary for using atrium widget before the widget finishes loading.
  (function() {
    const queue = [];
    const stub = {
      _queue: queue,
      authenticateCustomer: async function() { queue.push(['authenticateCustomer', arguments]); },
      on: function() { queue.push(['on', arguments]); }
    };
    window.AtriumWidget = stub;
  })();
</script>

<script
  src="https://uat-flowevx.s3.ap-southeast-1.amazonaws.com/atrium-widget/v1/widget.js"
  data-app-id="${appId}"
  async
></script>`;
  }, [appId]);

  useEffect(() => {
    if (!config) return;
    const domains = configuredDomains.length > 0 ? configuredDomains : [''];
    setDomainInputs(domains);

    const bundleIds = configuredBundleIds.length > 0 ? configuredBundleIds : [''];
    setBundleIdInputs(bundleIds);

    if (config.configuration?.style_override) {
      setStyleOverride({
        accentColor: config.configuration.style_override.accentColor || '#4f46e5',
        roundness: config.configuration.style_override.roundness || 'full',
        title: config.configuration.style_override.title || 'Live Support',
        position: config.configuration.style_override.position || 'bottom-right',
        horizontalOffset: config.configuration.style_override.horizontalOffset ?? 16,
        verticalOffset: config.configuration.style_override.verticalOffset ?? 16
      });
    }
  }, [config?.id, appId]);

  const loadConfig = async () => {
    setError(null);
    try {
      const resp = await httpClient.get<{ success: boolean; data: LiveChatConfigRow | null }>(
        '/api/channels/live-chat/config'
      );
      setConfig(resp.data || null);
    } catch (e: any) {
      setConfig(null);
      setError(
        e?.message || t('atrium.liveChat.errorLoad', 'Failed to load live chat configuration')
      );
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.post<{ success: boolean; data: LiveChatConfigRow }>(
        '/api/channels/live-chat/config'
      );
      setConfig(resp.data);
    } catch (e: any) {
      setError(e?.message || t('atrium.liveChat.errorEnable', 'Failed to enable live chat'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (
      !confirm(
        t(
          'atrium.liveChat.confirmRemove',
          'Remove live chat configuration? This will decommission the widget key.'
        )
      )
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await httpClient.delete<{ success: boolean }>('/api/channels/live-chat/config');
      setConfig(null);
    } catch (e: any) {
      setError(
        e?.message || t('atrium.liveChat.errorRemove', 'Failed to remove live chat configuration')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e: any) {
      setError(e?.message || t('atrium.liveChat.errorCopy', 'Failed to copy'));
    }
  };

  const normalizeDomainEntry = (raw: string): { value: string } | { error: string } | null => {
    const value = raw.trim();
    if (!value) return null;

    // Wildcard domain (subdomains only): https://*.example.com
    if (value.includes('*')) {
      const wildcardRe = /^https?:\/\/\*\.[a-z0-9.-]+(?::\d+)?$/i;
      if (!wildcardRe.test(value)) {
        return {
          error: t('atrium.liveChat.errorInvalidWildcard', {
            value,
            defaultValue: `Invalid wildcard domain: ${value}`
          })
        };
      }
      return { value };
    }

    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return {
          error: t('atrium.liveChat.errorOnlyHttp', {
            value,
            defaultValue: `Only http/https domains are allowed: ${value}`
          })
        };
      }
      // Keep it as an origin (no path/query/hash).
      if ((url.pathname && url.pathname !== '/') || url.search || url.hash) {
        return { value: url.origin };
      }
      return { value: url.origin };
    } catch {
      return {
        error: t('atrium.liveChat.errorInvalidUrl', {
          value,
          defaultValue: `Invalid domain URL: ${value}`
        })
      };
    }
  };

  const handleSaveConfiguration = async () => {
    setLoading(true);
    setError(null);
    try {
      const normalized: string[] = [];
      for (const raw of domainInputs) {
        const result = normalizeDomainEntry(raw);
        if (!result) continue;
        if ('error' in result) {
          throw new Error(result.error);
        }
        normalized.push(result.value);
      }

      const unique = Array.from(new Set(normalized));

      // Normalize bundle IDs (trim and filter empty)
      const normalizedBundleIds = bundleIdInputs
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      const uniqueBundleIds = Array.from(new Set(normalizedBundleIds));

      const resp = await httpClient.post<{ success: boolean; data: LiveChatConfigRow }>(
        '/api/channels/live-chat/config',
        {
          domains: unique,
          bundleIds: uniqueBundleIds,
          style_override: styleOverride
        }
      );
      setConfig(resp.data);
    } catch (e: any) {
      setError(e?.message || t('atrium.liveChat.errorSave', 'Failed to save configuration'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`p-6 rounded-lg border ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-4">
        <h3
          className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
        >
          {t('atrium.liveChat.title', 'Live Chat')}
        </h3>
        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {t(
            'atrium.liveChat.description',
            'Enable the widget key for partners to embed your Atrium live chat.'
          )}
        </p>
      </div>

      {error && (
        <div
          className={`mb-4 text-sm rounded p-3 ${
            theme === 'dark' ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'
          }`}
        >
          {error}
        </div>
      )}

      {!config ? (
        <div className="flex items-center gap-3">
          <button
            onClick={handleEnable}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              loading
                ? 'opacity-60 cursor-not-allowed'
                : theme === 'dark'
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {loading ? t('common.loading', 'Loading...') : t('common.enable', 'Enable')}
          </button>
          <button
            onClick={loadConfig}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div
              className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}
            >
              {t('atrium.liveChat.appId', 'App ID')}
            </div>
            <div
              className={`mt-1 p-3 rounded border text-sm font-mono break-all ${
                theme === 'dark'
                  ? 'border-gray-700 bg-gray-900 text-gray-200'
                  : 'border-gray-200 bg-gray-50 text-gray-900'
              }`}
            >
              {appId || '-'}
            </div>
          </div>

          <div>
            <div
              className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}
            >
              {t('atrium.liveChat.domains', 'Whitelisted domains')}
            </div>
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {t(
                'atrium.liveChat.domainsHelp',
                'Add allowed website origins. Use full origins like https://my-site.com or wildcard subdomains like https://*.my-site.com.'
              )}
            </p>

            <div className="mt-2 space-y-2">
              {domainInputs.map((val, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    value={val}
                    onChange={(e) =>
                      setDomainInputs((prev) =>
                        prev.map((v, i) => (i === idx ? e.target.value : v))
                      )
                    }
                    placeholder="https://my-domain.com"
                    className={`flex-1 px-3 py-2 rounded-md border text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-900 border-gray-700 text-gray-200'
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setDomainInputs((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={domainInputs.length <= 1 || loading}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      domainInputs.length <= 1 || loading
                        ? 'opacity-50 cursor-not-allowed'
                        : theme === 'dark'
                          ? 'bg-red-900/30 hover:bg-red-900/40 text-red-300'
                          : 'bg-red-50 hover:bg-red-100 text-red-700'
                    }`}
                  >
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              ))}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDomainInputs((prev) => [...prev, ''])}
                  disabled={loading}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    theme === 'dark'
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {t('common.add', 'Add')}
                </button>
              </div>
            </div>
          </div>

          <div>
            <div
              className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}
            >
              {t('atrium.liveChat.bundleIds', 'Whitelisted Bundle IDs')}
            </div>
            <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {t(
                'atrium.liveChat.bundleIdsHelp',
                'Add allowed mobile app bundle IDs. Use reverse domain notation like com.partner.acme for iOS/Android apps.'
              )}
            </p>

            <div className="mt-2 space-y-2">
              {bundleIdInputs.map((val, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    value={val}
                    onChange={(e) =>
                      setBundleIdInputs((prev) =>
                        prev.map((v, i) => (i === idx ? e.target.value : v))
                      )
                    }
                    placeholder="com.partner.acme"
                    className={`flex-1 px-3 py-2 rounded-md border text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-900 border-gray-700 text-gray-200'
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setBundleIdInputs((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={bundleIdInputs.length <= 1 || loading}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      bundleIdInputs.length <= 1 || loading
                        ? 'opacity-50 cursor-not-allowed'
                        : theme === 'dark'
                          ? 'bg-red-900/30 hover:bg-red-900/40 text-red-300'
                          : 'bg-red-50 hover:bg-red-100 text-red-700'
                    }`}
                  >
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              ))}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBundleIdInputs((prev) => [...prev, ''])}
                  disabled={loading}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    theme === 'dark'
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {t('common.add', 'Add')}
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div
              className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}
            >
              {t('atrium.liveChat.personalization', 'Personalization')}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {t('atrium.liveChat.widgetTitle', 'Widget Title')}
                </label>
                <input
                  type="text"
                  value={styleOverride.title}
                  onChange={(e) => setStyleOverride((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder={t('atrium.liveChat.defaultWidgetTitle', 'Live support')}
                  className={`w-full px-3 py-2 rounded-md border text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {t('atrium.liveChat.accentColor', 'Accent Color')}
                </label>
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="h-9 w-12 rounded border border-gray-300 dark:border-gray-700 p-1 bg-transparent cursor-pointer flex items-center justify-center"
                        style={{ backgroundColor: styleOverride.accentColor || '#000000' }}
                      >
                        <div className="size-full rounded-sm shadow-inner" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-none shadow-xl" align="start">
                      <Sketch
                        color={styleOverride.accentColor || '#000000'}
                        onChange={(color) =>
                          setStyleOverride((prev) => ({ ...prev, accentColor: color.hex }))
                        }
                      />
                    </PopoverContent>
                  </Popover>

                  <input
                    type="text"
                    value={styleOverride.accentColor}
                    onChange={(e) =>
                      setStyleOverride((prev) => ({ ...prev, accentColor: e.target.value }))
                    }
                    placeholder="#4f46e5"
                    className={`flex-1 px-3 py-2 rounded-md border text-sm font-mono ${
                      theme === 'dark'
                        ? 'bg-gray-900 border-gray-700 text-gray-200'
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label
                  className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {t('atrium.liveChat.roundness', 'Roundness')}
                </label>
                <select
                  value={styleOverride.roundness}
                  onChange={(e) =>
                    setStyleOverride((prev) => ({ ...prev, roundness: e.target.value as any }))
                  }
                  className={`w-full px-3 py-2 rounded-md border text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                >
                  <option value="none">
                    {t('atrium.liveChat.roundnessNone', 'None (Square)')}
                  </option>
                  <option value="normal">{t('atrium.liveChat.roundnessNormal', 'Normal')}</option>
                  <option value="full">
                    {t('atrium.liveChat.roundnessFull', 'Full (Rounded)')}
                  </option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {t('atrium.liveChat.widgetPosition', 'Widget Position')}
                </label>
                <select
                  value={styleOverride.position}
                  onChange={(e) =>
                    setStyleOverride((prev) => ({ ...prev, position: e.target.value as any }))
                  }
                  className={`w-full px-3 py-2 rounded-md border text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                >
                  <option value="bottom-right">
                    {t('atrium.liveChat.positionBottomRight', 'Bottom Right')}
                  </option>
                  <option value="bottom-left">
                    {t('atrium.liveChat.positionBottomLeft', 'Bottom Left')}
                  </option>
                  <option value="top-right">
                    {t('atrium.liveChat.positionTopRight', 'Top Right')}
                  </option>
                  <option value="top-left">
                    {t('atrium.liveChat.positionTopLeft', 'Top Left')}
                  </option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {t('atrium.liveChat.horizontalOffset', 'Horizontal Offset (px)')}
                </label>
                <input
                  type="number"
                  value={styleOverride.horizontalOffset}
                  onChange={(e) =>
                    setStyleOverride((prev) => ({
                      ...prev,
                      horizontalOffset: parseInt(e.target.value) || 0
                    }))
                  }
                  className={`w-full px-3 py-2 rounded-md border text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {t('atrium.liveChat.verticalOffset', 'Vertical Offset (px)')}
                </label>
                <input
                  type="number"
                  value={styleOverride.verticalOffset}
                  onChange={(e) =>
                    setStyleOverride((prev) => ({
                      ...prev,
                      verticalOffset: parseInt(e.target.value) || 0
                    }))
                  }
                  className={`w-full px-3 py-2 rounded-md border text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <p
              className={`mt-2 text-[10px] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
            >
              {t('atrium.liveChat.offsetHelp', 'Distance from the screen edges in pixels.')}
            </p>

            <div className="mt-4">
              <button
                type="button"
                onClick={handleSaveConfiguration}
                disabled={loading}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  loading
                    ? 'opacity-60 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                {loading
                  ? t('common.loading', 'Loading...')
                  : t('atrium.liveChat.saveConfig', 'Save Configuration')}
              </button>
            </div>
          </div>

          <div>
            <div
              className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}
            >
              {t('atrium.liveChat.embedSnippet', 'Embed snippet')}
            </div>
            <pre
              className={`mt-1 p-3 rounded border text-xs overflow-auto ${
                theme === 'dark'
                  ? 'border-gray-700 bg-gray-900 text-gray-200'
                  : 'border-gray-200 bg-gray-50 text-gray-900'
              }`}
            >
              {snippet}
            </pre>
            <div
              className={`my-2 text-xs flex items-center gap-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
            >
              <code
                className={`px-2 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}
              >
                async
              </code>
              <span>
                attribute is necessary for better load times without blocking your main app
              </span>
            </div>
            <hr className={theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} />
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleCopy}
                disabled={loading || !snippet}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                {copied ? t('common.copied', 'Copied') : t('common.copy', 'Copy')}
              </button>
              <button
                onClick={handleDisable}
                disabled={loading}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  theme === 'dark'
                    ? 'bg-red-900/30 hover:bg-red-900/40 text-red-300'
                    : 'bg-red-50 hover:bg-red-100 text-red-700'
                }`}
              >
                {t('common.remove', 'Remove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
